import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { GraduateInvite, GraduateInviteStatus } from '../../models/graduate-invite';
import { GraduateParticipant, GraduateParticipantStatus } from '../../models/graduate-participant';
import { userService } from '../../services/userService';
import { BulkGraduateInviteImportResult } from '../../models/bulk-import';
import { PaginatedResponse } from '../../models/paginatedResponse';
import styles from './AcademyManagement.module.css';

type ManagementTab = 'invites' | 'participants';
type PaginationMeta = Pick<PaginatedResponse<unknown>, 'totalCount' | 'pageSize' | 'pageNumber' | 'totalPages' | 'hasNext' | 'hasPrevious'>;

const ACADEMY_PAGE_SIZE = 50;

const formatDate = (value?: string) => value ? new Date(value).toLocaleString() : '—';

const getErrorMessage = (error: any, fallback: string) => {
  const response = error?.response?.data;
  return response?.errors?.[0] || response?.message || fallback;
};

const downloadInviteCsvTemplate = () => {
  const blob = new Blob(['email\nfirst.person@example.com\nsecond.person@example.com\n'], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'academy-invites-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

type PaginationControlsProps = {
  pagination: PaginationMeta | null;
  label: string;
  loading: boolean;
  onPageChange: (page: number) => void;
};

const PaginationControls = ({ pagination, label, loading, onPageChange }: PaginationControlsProps) => {
  if (!pagination || pagination.totalPages <= 1) return null;

  return (
    <div className={styles.pagination} aria-label={`${label} pagination`}>
      <div className={styles.paginationSummary}>
        <strong>Page {pagination.pageNumber} of {pagination.totalPages}</strong>
        <span>{pagination.totalCount.toLocaleString()} total {label.toLowerCase()}</span>
      </div>
      <div className={styles.paginationActions}>
        <button
          type="button"
          className={styles.paginationButton}
          onClick={() => onPageChange(pagination.pageNumber - 1)}
          disabled={!pagination.hasPrevious || loading}
        >
          Previous
        </button>
        <button
          type="button"
          className={styles.paginationButton}
          onClick={() => onPageChange(pagination.pageNumber + 1)}
          disabled={!pagination.hasNext || loading}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const AcademyManagement = () => {
  const [activeTab, setActiveTab] = useState<ManagementTab>('invites');
  const [email, setEmail] = useState('');
  const [invites, setInvites] = useState<GraduateInvite[]>([]);
  const [participants, setParticipants] = useState<GraduateParticipant[]>([]);
  const [invitePagination, setInvitePagination] = useState<PaginationMeta | null>(null);
  const [participantPagination, setParticipantPagination] = useState<PaginationMeta | null>(null);
  const [invitePage, setInvitePage] = useState(1);
  const [participantPage, setParticipantPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bulkInviteFile, setBulkInviteFile] = useState<File | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkGraduateInviteImportResult | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const bulkInviteInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inviteResponse, participantResponse] = await Promise.all([
        userService.fetchGraduateInvites(invitePage, ACADEMY_PAGE_SIZE, search || undefined),
        userService.fetchGraduateParticipants(participantPage, ACADEMY_PAGE_SIZE, search || undefined),
      ]);
      if (!inviteResponse.isSuccess || !inviteResponse.data || !participantResponse.isSuccess || !participantResponse.data) {
        throw new Error('Academy data could not be loaded.');
      }
      setInvites(inviteResponse.data.data);
      setParticipants(participantResponse.data.data);
      setInvitePagination(inviteResponse.data);
      setParticipantPagination(participantResponse.data);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Academy data could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [invitePage, participantPage, search]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const sendInvite = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    setNotice('');
    try {
      const response = await userService.createGraduateInvite({ email: email.trim() });
      if (!response.isSuccess) {
        throw new Error(response.message || 'The invitation could not be sent.');
      }
      setEmail('');
      setNotice(`Invitation sent to ${email.trim()}.`);
      await loadData();
    } catch (requestError: any) {
      setError(requestError?.message?.startsWith('The invitation')
        ? requestError.message
        : getErrorMessage(requestError, 'The invitation could not be sent.'));
    } finally {
      setSubmitting(false);
    }
  };

  const sendBulkInvites = async (event: FormEvent) => {
    event.preventDefault();
    if (!bulkInviteFile) return;

    setBulkSubmitting(true);
    setError('');
    setNotice('');
    setBulkResult(null);
    try {
      const response = await userService.createGraduateInvitesBulk(bulkInviteFile);
      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'The bulk invitations could not be sent.');
      }

      setBulkResult(response.data);
      setBulkInviteFile(null);
      if (bulkInviteInputRef.current) bulkInviteInputRef.current.value = '';
      setNotice(response.message || 'Bulk invitations processed.');
      await loadData();
    } catch (requestError: any) {
      setError(requestError?.message?.startsWith('Processed')
        ? requestError.message
        : getErrorMessage(requestError, 'The bulk invitations could not be sent.'));
    } finally {
      setBulkSubmitting(false);
    }
  };

  const revokeInvite = async (invite: GraduateInvite) => {
    if (!window.confirm(`Revoke the invitation for ${invite.email}?`)) return;
    setError('');
    setNotice('');
    try {
      const response = await userService.revokeGraduateInvite(invite.id);
      if (!response.isSuccess) throw new Error(response.message || 'The invitation could not be revoked.');
      setNotice(`Invitation for ${invite.email} revoked.`);
      await loadData();
    } catch (requestError: any) {
      setError(requestError?.message?.startsWith('The invitation')
        ? requestError.message
        : getErrorMessage(requestError, 'The invitation could not be revoked.'));
    }
  };

  const changeParticipantStatus = async (participant: GraduateParticipant) => {
    setError('');
    setNotice('');
    try {
      const response = participant.status === GraduateParticipantStatus.Suspended
        ? await userService.approveGraduateParticipant(participant.id)
        : await userService.suspendGraduateParticipant(participant.id);
      if (!response.isSuccess) throw new Error(response.message || 'Participant status could not be updated.');
      setNotice(`${participant.fullName || participant.email} is now ${participant.status === GraduateParticipantStatus.Suspended ? 'active' : 'suspended'}.`);
      await loadData();
    } catch (requestError: any) {
      setError(requestError?.message?.startsWith('Participant')
        ? requestError.message
        : getErrorMessage(requestError, 'Participant status could not be updated.'));
    }
  };

  const updateSearch = (value: string) => {
    setSearch(value);
    setInvitePage(1);
    setParticipantPage(1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.intro}>
        <div>
          <p className={styles.kicker}>ACADEMY OPERATIONS</p>
          <h2>Welcome people into the programme</h2>
          <p>Send secure invitations, track onboarding, and keep the participant directory healthy. Content, assignments, and reports stay in this Academy backoffice.</p>
        </div>
        <div className={styles.statBlock}>
          <span>{participantPagination?.totalCount ?? participants.length}</span>
          <small>participants total</small>
        </div>
      </div>

      <div className={styles.inviteCard}>
        <div>
          <h3>Invite a graduate</h3>
          <p>The invitee receives a one-time code by email, then completes their own registration.</p>
        </div>
        <form className={styles.inviteForm} onSubmit={sendInvite}>
          <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="candidate@company.com" aria-label="Candidate email" required />
          <button type="submit" disabled={submitting}>{submitting ? 'Sending…' : 'Send invite'}</button>
        </form>
      </div>

      <div className={styles.bulkCard}>
        <div>
          <h3>Invite from CSV</h3>
          <p>Upload one email per row, or a CSV with an <code>email</code> column. Duplicate addresses are collapsed automatically.</p>
        </div>
        <form className={styles.bulkForm} onSubmit={sendBulkInvites}>
          <input
            ref={bulkInviteInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={event => setBulkInviteFile(event.target.files?.[0] || null)}
            aria-label="CSV file containing graduate emails"
          />
          <button type="submit" disabled={!bulkInviteFile || bulkSubmitting}>
            {bulkSubmitting ? 'Processing…' : 'Send CSV invites'}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={downloadInviteCsvTemplate}>
            Download template
          </button>
        </form>
      </div>

      {bulkResult && (
        <div className={styles.importSummary}>
          <div className={styles.summaryHeader}>
            <div>
              <strong>CSV import complete</strong>
              <span>{bulkResult.emailsSent} invitation{bulkResult.emailsSent === 1 ? '' : 's'} sent</span>
            </div>
            {bulkResult.emailFailures > 0 && <span className={styles.summaryWarning}>{bulkResult.emailFailures} email failure{bulkResult.emailFailures === 1 ? '' : 's'}</span>}
          </div>
          <div className={styles.summaryGrid}>
            <div><strong>{bulkResult.rowsRead}</strong><span>rows read</span></div>
            <div><strong>{bulkResult.uniqueEmails}</strong><span>unique emails</span></div>
            <div><strong>{bulkResult.invitesCreated}</strong><span>invites created</span></div>
            <div><strong>{bulkResult.existingAccountsSkipped}</strong><span>existing accounts skipped</span></div>
          </div>
          {bulkResult.issues.length > 0 && (
            <details className={styles.issueDetails}>
              <summary>{bulkResult.issues.length} row detail{bulkResult.issues.length === 1 ? '' : 's'}</summary>
              <ul>
                {bulkResult.issues.map((issue, index) => (
                  <li key={`${issue.rowNumber}-${index}`}>Row {issue.rowNumber}: {issue.reason}{issue.value ? ` (${issue.value})` : ''}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {notice && <div className={styles.notice}>{notice}</div>}
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.toolbar}>
        <div className={styles.tabs}>
          <button className={activeTab === 'invites' ? styles.activeTab : ''} onClick={() => setActiveTab('invites')}>Invitations <span>{invitePagination?.totalCount ?? invites.length}</span></button>
          <button className={activeTab === 'participants' ? styles.activeTab : ''} onClick={() => setActiveTab('participants')}>Participants <span>{participantPagination?.totalCount ?? participants.length}</span></button>
        </div>
        <div className={styles.actions}>
          <input value={search} onChange={event => updateSearch(event.target.value)} placeholder="Search email or name" aria-label="Search Academy records" />
          <button onClick={() => void loadData()} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      {loading ? <div className={styles.loading}>Loading Academy records…</div> : activeTab === 'invites' ? (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Email</th><th>Status</th><th>Sent</th><th>Expires</th><th>Invited by</th><th /></tr></thead>
              <tbody>
                {invites.length === 0 ? <tr><td colSpan={6} className={styles.empty}>No invitations match this search.</td></tr> : invites.map(invite => (
                  <tr key={invite.id}>
                    <td className={styles.primaryCell}>{invite.email}</td>
                    <td><span className={`${styles.badge} ${styles[`status${invite.status}`]}`}>{invite.status}</span></td>
                    <td>{formatDate(invite.createdAtUtc)}</td>
                    <td>{formatDate(invite.expiresAtUtc)}</td>
                    <td>{invite.invitedByUserName}</td>
                    <td>{invite.status === GraduateInviteStatus.Pending && <button className={styles.textButton} onClick={() => void revokeInvite(invite)}>Revoke</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls
            pagination={invitePagination}
            label="invitations"
            loading={loading}
            onPageChange={page => setInvitePage(page)}
          />
        </>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Name</th><th>Email</th><th>Department</th><th>Cohort</th><th>Status</th><th /></tr></thead>
              <tbody>
                {participants.length === 0 ? <tr><td colSpan={6} className={styles.empty}>No participants match this search.</td></tr> : participants.map(participant => (
                  <tr key={participant.id}>
                    <td className={styles.primaryCell}>{participant.fullName}</td>
                    <td>{participant.email}</td>
                    <td>{participant.department || '—'}</td>
                    <td>{participant.cohort || '—'}</td>
                    <td><span className={`${styles.badge} ${styles[`status${participant.status}`]}`}>{participant.status}</span></td>
                    <td>{participant.status !== GraduateParticipantStatus.Rejected && <button className={styles.textButton} onClick={() => void changeParticipantStatus(participant)}>{participant.status === GraduateParticipantStatus.Suspended ? 'Restore' : 'Suspend'}</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls
            pagination={participantPagination}
            label="participants"
            loading={loading}
            onPageChange={page => setParticipantPage(page)}
          />
        </>
      )}
    </div>
  );
};

export default AcademyManagement;
